/**
 * @fileoverview Bulk-clearing missing-notes notices from any of the
 * organization's lists: the dashboard's Lessons tab, a program's Sessions tab
 * and missing-notes card, and a class's Lessons tab (lib/notesClearance).
 *
 * An admin can tick any past session that owes notes, or whose notice was
 * cleared, and:
 *
 * - **Chase** the notes (C4, decided Nov 1, 2026): each of the ticked
 *   sessions' classes is chased by the class page's own rule, in the
 *   instructors' updates and never by email. It leads the bar, ahead of
 *   Clear, and asks nothing first, as the class page's Chase notes does not.
 * - **Clear** the notices: they stop counting as missing for the org and for
 *   the instructors. Clearing one an instructor asked about approves the ask.
 * - **Decline** instructors' requests: the sessions keep owing notes and each
 *   instructor who asked is told why, when a note is given.
 * - **Restore** cleared sessions: back to owing notes on both sides.
 *
 * Nothing here marks a session taught; it only stops or restarts the notice.
 *
 * `useNotesClearActions` is that set of actions for any selection, so a list
 * that ticks every session (useOrgSessionActions) offers the same three for
 * the ticked sessions they fit; `useOrgNotesClear` is the program Overview's
 * missing-notes card, where only those sessions can be ticked.
 */

import { useMemo, useState } from "react";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import useRowSelection from "@/hooks/useRowSelection";
import { NotesClearDialog, NotesClearToolbar, SelectBox } from "@/components/studio/NotesClearing";
import { isCleared, isRequested, orgRef, plural, sendInPages, toastResult } from "@/lib/notesClearance";
import { NOTES_CHASE_BATCH, chaseOutcome, chaseReach } from "@/lib/orgSessions";

const PATHS = {
  clear: ["/org/notes-clearances", "cleared"],
  decline: ["/org/notes-clearances/decline", "declined"],
  restore: ["/org/notes-clearances/restore", "restored"],
};

/** Whether the org can act on this row's notice: it owes notes, or it was cleared. */
export const notesActionable = (row) => (row.missing || isCleared(row)) && row.class_id && (row.date || row.scheduled_date);

/**
 * Chase, Clear, Decline and Restore for the ticked rows they fit, with the
 * confirm dialogs of the three that ask.
 *
 * @param {{ chosen: Array<object>, onDone: () => void, onChanged?: () => void, testId: string }} options
 *   `chosen` is every ticked row; `onDone` clears the selection after a run.
 */
export function useNotesClearActions({ chosen, onDone, onChanged, testId }) {
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);

  const acting = chosen.filter(notesActionable);
  const owing = acting.filter((r) => !isCleared(r));
  const asked = owing.filter(isRequested);
  const cleared = acting.filter(isCleared);

  const run = async (kind, note) => {
    const [path, field] = PATHS[kind];
    const targets = kind === "clear" ? owing : kind === "decline" ? asked : cleared;
    setBusy(true);
    try {
      const result = await sendInPages(
        targets.map(orgRef),
        (page) => orgApi.post(path, { sessions: page, ...(note ? { note } : {}) }),
        [field],
      );
      const n = result[field].length;
      const message =
        kind === "clear"
          ? `${plural(n, "notice")} cleared${result.approved ? `, ${result.approved} of them as asked` : ""}`
          : kind === "decline"
            ? `${plural(n, "request")} declined. The instructors were told.`
            : `${plural(n, "session")} back on the missing-notes list`;
      toastResult(message, n, result.skipped);
      onDone();
      setDialog(null);
      onChanged?.();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't update those sessions"));
      if (err.partial) onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  // The classes of the ticked sessions that owe notes, in the server's pages.
  // A page whose classes owe nothing answers 409, which is an answer.
  const reach = chaseReach(owing);
  const chase = async () => {
    setBusy(true);
    const reached = new Map();
    let refused = null;
    try {
      for (let i = 0; i < reach.classIds.length; i += NOTES_CHASE_BATCH) {
        try {
          const res = await orgApi.post("/org/notes-chase", { class_ids: reach.classIds.slice(i, i + NOTES_CHASE_BATCH) });
          (res.data?.chased || []).forEach((person) => reached.set(person.user_id, person));
        } catch (err) {
          if (err?.response?.status !== 409) throw err;
          refused = err;
        }
      }
      if (!reached.size && refused) toast(getOrgErrorMessage(refused, "None of these classes owe notes."));
      else toast.success(chaseOutcome([...reached.values()]));
      onDone();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, reached.size ? "Couldn't send every reminder" : "Couldn't send the reminder"));
    } finally {
      setBusy(false);
    }
  };

  const actions = [
    owing.length > 0 && {
      id: "chase",
      label: reach.known ? `Chase ${plural(reach.instructors, "instructor")}` : "Chase notes",
      primary: true,
      onPress: chase,
      disabled: busy,
    },
    owing.length > 0 && {
      id: "clear",
      label: `Clear ${plural(owing.length, "notice")}`,
      onPress: () => setDialog("clear"),
    },
    asked.length > 0 && {
      id: "decline",
      label: `Decline ${plural(asked.length, "request")}`,
      onPress: () => setDialog("decline"),
    },
    cleared.length > 0 && {
      id: "restore",
      // "notice", so it never reads as restoring an archived session beside it.
      label: `Restore ${plural(cleared.length, "notice")}`,
      onPress: () => run("restore"),
      disabled: busy,
    },
  ].filter(Boolean);

  const dialogs = (
    <>
      <NotesClearDialog
        open={dialog === "clear"}
        onOpenChange={(open) => !open && setDialog(null)}
        title={`Clear ${plural(owing.length, "missing-notes notice")}?`}
        description="These sessions stop counting as missing notes, for you and for their instructors. Nothing is marked as taught, and you can restore them later."
        noteLabel="Note (optional)"
        notePlaceholder="e.g. School assembly"
        confirmLabel="Clear notices"
        busy={busy}
        onConfirm={(note) => run("clear", note)}
        testId={`${testId}-dialog`}
      >
        {asked.length > 0 && (
          <p className="font-sans text-[14px] text-[var(--text-secondary)]" data-testid={`${testId}-approves`}>
            {asked.length === 1
              ? "An instructor asked to clear 1 of these. Clearing approves the request."
              : `Instructors asked to clear ${asked.length} of these. Clearing approves their requests.`}
          </p>
        )}
      </NotesClearDialog>
      <NotesClearDialog
        open={dialog === "decline"}
        onOpenChange={(open) => !open && setDialog(null)}
        title={`Decline ${plural(asked.length, "request")}?`}
        description="These sessions keep needing notes. Each instructor who asked is told in their updates."
        noteLabel="Tell them why (optional)"
        confirmLabel="Decline"
        busy={busy}
        onConfirm={(note) => run("decline", note)}
        testId={`${testId}-decline`}
      />
    </>
  );

  return { actions, dialogs };
}

/**
 * @param {{
 *   rows: Array<object>, onChanged?: () => void,
 *   keyOf?: (row: object) => string, testId?: string,
 * }} options rows are the visible rows of the list, in its shape
 *   (`sessionRow`, `missingRow`, or a notes-owed row with `missing: true`).
 */
export default function useOrgNotesClear({ rows, onChanged, keyOf = (r) => r.lesson_id, testId = "org-notes-clear" }) {
  const acting = useMemo(() => rows.filter(notesActionable), [rows]);
  const byKey = useMemo(() => new Map(acting.map((r) => [keyOf(r), r])), [acting, keyOf]);
  const selection = useRowSelection(acting.map(keyOf));

  const chosen = [...selection.selected].map((k) => byKey.get(k)).filter(Boolean);
  const { actions, dialogs } = useNotesClearActions({ chosen, onDone: selection.clear, onChanged, testId });

  const checkbox = (row) => {
    const key = keyOf(row);
    if (!byKey.has(key)) return null;
    return (
      <SelectBox
        checked={selection.selected.has(key)}
        onChange={() => selection.toggle(key)}
        label={`Select ${row.class_name || row.title || row.display_name || "session"} on ${row.date || row.scheduled_date}`}
        testId={`${testId}-row-${key}`}
      />
    );
  };

  const toolbar = (
    <NotesClearToolbar
      selection={selection}
      actions={actions}
      hint="Tick sessions to clear their missing-notes notice."
      testId={testId}
    />
  );

  return { checkbox, toolbar, dialogs, selection };
}
